import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Users, Settings, TrendingUp, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (user.role !== 'admin') {
      toast.error('Access denied: Admin only');
      navigate('/dashboard');
      return;
    }
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [usersRes, childrenRes, testsRes, resultsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/children`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/tests`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { tests: [] } })),
        axios.get(`${API}/admin/results`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);

      setStats({
        totalUsers: usersRes.data?.users?.length || 0,
        totalChildren: childrenRes.data?.children?.length || 0,
        totalTests: testsRes.data?.tests?.length || 0,
        totalResults: resultsRes.data?.results?.length || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({
        totalUsers: 0,
        totalChildren: 0,
        totalTests: 6,
        totalResults: 0
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><p>Loading...</p></div>;
  }

  return (
    <div className="min-h-screen p-4 md:p-8 lg:p-12">
      <div className="max-w-7xl mx-auto">
        <Button data-testid="back-btn" variant="ghost" onClick={() => navigate('/')} className="mb-6 rounded-full">
          <ArrowLeft className="mr-2 h-4 w-4" /> Logout
        </Button>

        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold mb-2">Admin Panel</h1>
          <p className="text-muted-foreground">Platform overview and management</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card-base p-6">
            <div className="flex items-center justify-between mb-4">
              <Users className="h-8 w-8 text-primary" />
              <span className="text-3xl font-bold">{stats.totalUsers}</span>
            </div>
            <h3 className="font-semibold text-sm text-muted-foreground">Total Users</h3>
          </div>
          <div className="card-base p-6">
            <div className="flex items-center justify-between mb-4">
              <Users className="h-8 w-8 text-secondary" />
              <span className="text-3xl font-bold">{stats.totalChildren}</span>
            </div>
            <h3 className="font-semibold text-sm text-muted-foreground">Total Children</h3>
          </div>
          <div className="card-base p-6">
            <div className="flex items-center justify-between mb-4">
              <Settings className="h-8 w-8 text-accent" />
              <span className="text-3xl font-bold">{stats.totalTests}</span>
            </div>
            <h3 className="font-semibold text-sm text-muted-foreground">Total Tests</h3>
          </div>
          <div className="card-base p-6">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="h-8 w-8 text-primary" />
              <span className="text-3xl font-bold">{stats.totalResults}</span>
            </div>
            <h3 className="font-semibold text-sm text-muted-foreground">Tests Completed</h3>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="card-base p-6">
            <h2 className="font-heading text-2xl font-bold mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <Button className="w-full rounded-full justify-start" variant="outline" onClick={() => navigate('/test-admin')}>
                <Settings className="mr-2 h-4 w-4" /> Manage Tests
              </Button>
              <Button className="w-full rounded-full justify-start" variant="outline">
                <Users className="mr-2 h-4 w-4" /> View All Users
              </Button>
              <Button className="w-full rounded-full justify-start" variant="outline">
                <TrendingUp className="mr-2 h-4 w-4" /> Platform Analytics
              </Button>
            </div>
          </div>

          <div className="card-base p-6">
            <h2 className="font-heading text-2xl font-bold mb-4">System Status</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Backend API</span>
                <span className="px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-semibold">Online</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Database</span>
                <span className="px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-semibold">Connected</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Payment System</span>
                <span className="px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-semibold">Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;